'use client';

/**
 * Cockpit-themed AMTIL control surfaces, shared across the Encyclopedia module.
 * CDU "black key" hardware look — same family as chrome/CockpitKey.tsx (the
 * approved DAY/NGT + PANEL header keys): charcoal bezel, steel edge, inset
 * black key face, engraved micro-label, green annunciator when engaged.
 * Gold appears only on the saved bookmark (gold = CTA/saved state).
 */
import React from 'react';
import { Bookmark, Check, ChevronDown, Maximize2 } from 'lucide-react';
import styles from './cockpit.module.css';
import { AxisPlane } from './AxisPlane';
export { AxisPlane, navPlaneHeadingForScrollDelta, navPlaneHeading } from './AxisPlane';
export { RunwayNavScrollbar } from './RunwayNavScrollbar';

type PushProps = {
  label: string;
  icon?: React.ReactNode;
  lit?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
};

export function CockpitButton({ label, icon, lit, onClick, className, style, ...rest }: PushProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={typeof lit === 'boolean' ? lit : undefined}
      className={`${styles.push} ${lit ? styles.lit : ''} ${className ?? ''}`}
      style={style}
      {...rest}
    >
      <span className={styles.face}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <span className={styles.pushLabel}>{label}</span>
      </span>
    </button>
  );
}

type IconBtnProps = {
  onClick?: () => void;
  children: React.ReactNode;
  /** Engraved micro-label under the glyph, e.g. "FULL", "SAVE". */
  label?: string;
  gold?: boolean;
  lit?: boolean;
  small?: boolean;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  'aria-label': string;
};

export function CockpitIconButton({ onClick, children, label, gold, lit, small, pulse, className, style, title, ...rest }: IconBtnProps & { pulse?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={typeof lit === 'boolean' ? lit : undefined}
      className={`${styles.iconBtn} ${gold ? styles.gold : ''} ${lit ? styles.lit : ''} ${small ? styles.sm : ''} ${pulse ? styles.pulse : ''} ${className ?? ''}`}
      style={style}
      {...rest}
    >
      <span className={styles.face}>
        {children}
        {label && <span className={styles.iconLabel}>{label}</span>}
      </span>
    </button>
  );
}

type ToggleProps = {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
};

export function CockpitToggle({ label, checked, onChange, className, style }: ToggleProps) {
  return (
    <div className={`${styles.toggle} ${className ?? ''}`} style={style}>
      <span className={styles.toggleLabel}>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`Toggle ${label}`}
        onClick={() => onChange(!checked)}
        className={`${styles.track} ${checked ? styles.on : ''}`}
      >
        <span className={styles.knob} />
      </button>
    </div>
  );
}

/** Fullscreen / expand key — engraved FULL label. */
export function CockpitFullscreenButton({ onClick, className, style }: { onClick?: () => void; className?: string; style?: React.CSSProperties }) {
  return (
    <CockpitIconButton onClick={onClick} aria-label="Toggle fullscreen" title="Fullscreen" label="FULL" className={className} style={style}>
      <Maximize2 strokeWidth={2.2} />
    </CockpitIconButton>
  );
}

/**
 * Bookmark control used everywhere in the Encyclopedia. Gold annunciator +
 * filled glyph when saved. `small` for dense card contexts.
 */
export function CockpitBookmarkButton({
  saved,
  onClick,
  small,
  className,
  style,
}: {
  saved: boolean;
  onClick?: () => void;
  small?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <CockpitIconButton
      onClick={onClick}
      gold={saved}
      small={small}
      label={saved ? 'SAVED' : 'SAVE'}
      aria-label={saved ? 'Remove bookmark' : 'Add bookmark'}
      title={saved ? 'Bookmarked' : 'Bookmark'}
      className={className}
      style={style}
    >
      <Bookmark strokeWidth={saved ? 1.5 : 2} fill={saved ? 'currentColor' : 'none'} />
    </CockpitIconButton>
  );
}

/** Machined hotspot ID-plate. Collapsed = red indicator; expanded = compact indicator + title. */
export function CockpitChip({
  label,
  active,
  expanded = false,
}: {
  label: string;
  active?: boolean;
  expanded?: boolean;
}) {
  return (
    <span className={`${styles.chip} ${expanded ? styles.chipExpanded : ''} ${active ? styles.active : ''}`} aria-hidden>
      <span className={`${styles.chipText} ${styles.chipMirror}`} aria-hidden>
        {label}
      </span>
      <span className={styles.chipDot} />
      <span className={`${styles.chipText} ${styles.chipLabel}`}>{label}</span>
    </span>
  );
}

/** Dark metallic plate panel (annotation popups, dropdown menus). */
export function CockpitPanel({
  children,
  className,
  style,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`${styles.panel} ${className ?? ''}`} style={style} {...rest}>
      {children}
    </div>
  );
}

/**
 * Runway progress bar — navy strip with dashed gold centerline; the solid
 * AMTIL-blue plane taxis with playback. Click or ArrowLeft/ArrowRight (±5s)
 * to seek. Shared by every encyclopedia audio player.
 */
function formatRunwayTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function RunwayProgressBar({
  currentTime,
  duration,
  onSeek,
  minTime = 0,
  disabled = false,
  appearance = 'classic',
  showTimes = true,
  height = 28,
  planeSize = 32,
  ariaLabel = 'Audio position on the runway',
  className,
  style,
}: {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  minTime?: number;
  disabled?: boolean;
  appearance?: 'classic' | 'instrument';
  showTimes?: boolean;
  height?: number;
  planeSize?: number;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const total = Number.isFinite(duration) ? Math.max(0, duration) : 0;
  const minimum = Number.isFinite(minTime) ? Math.min(total, Math.max(0, minTime)) : 0;
  const position = Math.min(total, Math.max(minimum, Number.isFinite(currentTime) ? currentTime : minimum));
  const span = total - minimum;
  const inactive = disabled || span <= 0;
  const pct = span > 0 ? (position - minimum) / span : 0;
  const remaining = total - position;
  const seekTo = (time: number) => { if (!inactive) onSeek(Math.min(Math.max(time, minimum), total)); };
  const timeAt = (fraction: number) => minimum + fraction * span;
  // Inset clears the runway-number times at each threshold.
  const instrument = appearance === 'instrument';
  const inset = instrument ? 26 : planeSize / 2 + 36;
  // Magnifier (director 2026-09-26): hovering enlarges the deck and shows a ghost plane
  // + time where a click/drag would land, so choosing a spot is easy. Drag scrubs.
  const [hoverPct, setHoverPct] = React.useState<number | null>(null);
  const dragging = React.useRef(false);
  React.useEffect(() => {
    if (inactive) { dragging.current = false; setHoverPct(null); }
  }, [inactive]);
  const pctAt = (el: HTMLElement, clientX: number) => {
    const rect = el.getBoundingClientRect();
    const usable = Math.max(1, rect.width - inset * 2);
    return Math.min(1, Math.max(0, (clientX - rect.left - inset) / usable));
  };
  const lift = !instrument && !inactive && hoverPct !== null ? 10 : 0;
  return (
    <div
      role="slider"
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive}
      aria-label={ariaLabel}
      aria-valuemin={minimum}
      aria-valuemax={total}
      aria-valuenow={position}
      aria-valuetext={`${formatRunwayTime(position)} of ${formatRunwayTime(total)}`}
      className={`${styles.runwayBar} ${instrument ? styles.runwayInstrument : ''} ${!instrument && !inactive && hoverPct !== null ? styles.runwayZoom : ''} ${className ?? ''}`}
      data-show-times={showTimes ? 'true' : 'false'}
      style={{ height: height + lift, marginBlock: -lift / 2, ...style }}
      onPointerDown={(e) => {
        if (inactive) return;
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        seekTo(timeAt(pctAt(e.currentTarget, e.clientX)));
      }}
      onPointerMove={(e) => {
        if (inactive) return;
        const at = pctAt(e.currentTarget, e.clientX);
        if (e.pointerType === 'mouse') setHoverPct(at);
        if (dragging.current) seekTo(timeAt(at));
      }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
      onPointerLeave={() => { if (!dragging.current) setHoverPct(null); }}
      onKeyDown={(e) => {
        if (inactive) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); seekTo(position + 5); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); seekTo(position - 5); }
      }}
    >
      {showTimes && <span className={`${styles.runwayTime} ${styles.runwayTimeL}`}>{formatRunwayTime(position)}</span>}
      {showTimes && <span className={`${styles.runwayTime} ${styles.runwayTimeR}`}>{formatRunwayTime(remaining)}</span>}
      {hoverPct !== null && !inactive && (
        <>
          <AxisPlane
            className={`${styles.runwayPlane} ${styles.runwayGhost}`}
            style={{ width: planeSize, height: planeSize, left: `calc(${inset}px + ${hoverPct} * (100% - ${inset * 2}px))` }}
          />
          <span className={styles.runwayHoverTime} style={{ left: `calc(${inset}px + ${hoverPct} * (100% - ${inset * 2}px))` }}>
            {formatRunwayTime(timeAt(hoverPct))}
          </span>
        </>
      )}
      <AxisPlane
        className={styles.runwayPlane}
        style={{
          width: planeSize + lift,
          height: planeSize + lift,
          left: `calc(${inset}px + ${pct} * (100% - ${inset * 2}px))`,
        }}
      />
    </div>
  );
}

/** Windsock that still reads as a speaker: cone = driver, sock = horn, ripples = sound. */
export function WindsockButton({
  muted,
  onClick,
  className,
  style,
}: {
  muted: boolean;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!muted}
      aria-label={muted ? 'Unmute audio' : 'Mute audio'}
      title={muted ? 'Muted' : 'Volume'}
      className={`${styles.windsock} ${className ?? ''}`}
      style={style}
    >
      <svg width="100%" height="100%" viewBox="0 0 36 36" fill="none" aria-hidden>
        {/* Director 2026-09-27: reversed (mast on the right, sock streaming left), a tapered
            cone, and sound ripples off the open end — a speaker icon that is also a windsock. */}
        <rect x="24.9" y="4" width="2.6" height="29" rx="1.3" fill="var(--windsock-mast)" />
        <circle cx="26.2" cy="4.3" r="2.2" fill="var(--windsock-ball)" stroke="var(--windsock-edge)" strokeWidth="0.6" />
        {muted ? (
          <>
            {/* Limp sock hanging off the mast, struck through */}
            <path d="M24.4 5.6 L17.4 8.6 L18.6 21.5 L24.4 17.4 Z" fill="var(--windsock-orange)" opacity="0.5" stroke="var(--windsock-edge)" strokeWidth="0.7" />
            <path d="M23.2 6.6 L5 25" stroke="var(--windsock-ring)" strokeWidth="1.9" strokeLinecap="round" />
          </>
        ) : (
          <>
            {/* Inflated airfield sock, 5 alternating bands, tapering to a point like a speaker cone */}
            <path d="M24.40 3.80 L20.02 5.10 L20.02 16.90 L24.40 18.20 Z" fill="var(--windsock-orange)" />
            <path d="M20.02 5.10 L15.64 6.40 L15.64 15.60 L20.02 16.90 Z" fill="var(--windsock-white)" />
            <path d="M15.64 6.40 L11.26 7.70 L11.26 14.30 L15.64 15.60 Z" fill="var(--windsock-orange)" />
            <path d="M11.26 7.70 L6.88 9.00 L6.88 13.00 L11.26 14.30 Z" fill="var(--windsock-white)" />
            <path d="M6.88 9.00 L2.50 10.30 L2.50 11.70 L6.88 13.00 Z" fill="var(--windsock-orange)" />
            <path d="M24.40 3.80 L2.50 10.30 L2.50 11.70 L24.40 18.20 Z" stroke="var(--windsock-edge)" strokeWidth="0.7" strokeLinejoin="round" />
            {/* Sound ripples to the right, growing outward */}
            <path d="M28.6 9 Q29.7 11 28.6 13" stroke="var(--windsock-ring)" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M30.8 7 Q32.8 11 30.8 15" stroke="var(--windsock-ring)" strokeWidth="1.4" strokeLinecap="round" opacity="0.8" />
            <path d="M33 5 Q35.9 11 33 17" stroke="var(--windsock-ring)" strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
          </>
        )}
      </svg>
    </button>
  );
}

/** Connect NameChip taxiway plate — gold / navy, thick chevron. */
export function TaxiwayIdButton({
  expanded,
  onClick,
  className,
  style,
}: {
  expanded: boolean;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={expanded ? 'Collapse audio controls' : 'Expand audio controls'}
      title={expanded ? 'Collapse' : 'Expand'}
      className={`${styles.taxiwayId} ${className ?? ''}`}
      style={style}
    >
      <svg width="18" height="12" viewBox="0 0 18 12" aria-hidden>
        {expanded ? (
          <path d="M1 2.2 L9 10.4 L17 2.2" fill="none" stroke="#022233" strokeWidth="3.2" strokeLinecap="square" strokeLinejoin="miter" />
        ) : (
          <path d="M1 9.8 L9 1.6 L17 9.8" fill="none" stroke="#022233" strokeWidth="3.2" strokeLinecap="square" strokeLinejoin="miter" />
        )}
      </svg>
    </button>
  );
}

export type CockpitSelectOption = { id: string; label: string; value: string };

/** Focus an option without moving the page, then scroll only the menu's own scroller so it shows. */
function focusOption(option: HTMLElement | undefined, root: HTMLElement | null) {
  if (!option) return;
  option.focus({ preventScroll: true });
  let scroller = option.parentElement;
  while (scroller && scroller !== root && scroller.scrollHeight <= scroller.clientHeight) scroller = scroller.parentElement;
  if (!scroller || scroller === root) return;
  const at = option.getBoundingClientRect();
  const box = scroller.getBoundingClientRect();
  if (at.top < box.top) scroller.scrollTop -= box.top - at.top;
  else if (at.bottom > box.bottom) scroller.scrollTop += at.bottom - box.bottom;
}
/**
 * Inside the boarding door only: scroll the door's own scroller just enough that the open menu sits in
 * its scroll-padding band, clear of the pinned Back and handle. Outside a door, and past the door's
 * edge, nothing is scrolled.
 */
function revealMenu(menu: HTMLElement | null | undefined, root: HTMLElement | null) {
  const door = root?.closest('[data-door-shell]');
  if (!menu || !door) return;
  let scroller = root?.parentElement ?? null;
  while (scroller && scroller !== door && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) scroller = scroller.parentElement;
  if (!scroller || scroller === door) return;
  const style = getComputedStyle(scroller);
  const box = scroller.getBoundingClientRect();
  const top = box.top + (parseFloat(style.scrollPaddingTop) || 0);
  const bottom = box.bottom - (parseFloat(style.scrollPaddingBottom) || 0);
  const at = menu.getBoundingClientRect();
  if (at.bottom > bottom) scroller.scrollTop += Math.min(at.bottom - bottom, at.top - top);
  else if (at.top < top) scroller.scrollTop -= top - at.top;
}
const optionsIn = (root: HTMLElement | null) => Array.from(root?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);

/**
 * Variant selector as a cockpit key: engraved value + chevron, dark metallic
 * dropdown plate. Parent owns open state so modal/tile mounts can reset it.
 */
export function CockpitSelect({
  options,
  value,
  onChange,
  open,
  onOpenChange,
  ariaLabel,
  menuPlacement = 'down',
  className,
  style,
}: {
  options: CockpitSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  ariaLabel: string;
  menuPlacement?: 'up' | 'down';
  className?: string;
  style?: React.CSSProperties;
}) {
  const selected = options.find((o) => o.value === value) || options[0];
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const closeRef = React.useRef(onOpenChange);
  closeRef.current = onOpenChange;

  // Opening moves focus to the current option; a press outside closes the menu.
  React.useEffect(() => {
    if (!open) return;
    revealMenu(rootRef.current?.querySelector<HTMLElement>('[role="listbox"]')?.parentElement, rootRef.current);
    const buttons = optionsIn(rootRef.current);
    focusOption(buttons.find((b) => b.getAttribute('aria-selected') === 'true') ?? buttons[0], rootRef.current);
    const away = (event: PointerEvent) => { if (!rootRef.current?.contains(event.target as Node)) closeRef.current(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);
  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: 'relative', ...style }}
      onKeyDown={(e) => {
        // Escape closes the dropdown WITHOUT bubbling to overlay handlers
        // (inside the 3D fullscreen modal, an unhandled Escape closes the
        // whole modal while the dropdown is open).
        if (e.key === 'Escape' && open && !e.defaultPrevented) {
          // The App Router root listens on document, so stopPropagation alone doesn't reach
          // document-level modal handlers; they skip defaultPrevented (BevelModal, TermSidebar).
          e.preventDefault();
          e.stopPropagation();
          onOpenChange(false);
          triggerRef.current?.focus();
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
          // Leave from the trigger so Tab continues after it (the focused option unmounts).
          if (e.key === 'Tab' && open) { triggerRef.current?.focus(); onOpenChange(false); }
          return;
        }
        e.preventDefault();
        if (!open) { onOpenChange(true); return; }
        const buttons = optionsIn(rootRef.current);
        const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? buttons.length - 1
          : e.key === 'ArrowDown' ? Math.min(buttons.length - 1, at + 1) : Math.max(0, at - 1);
        focusOption(buttons[next], rootRef.current);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        // The name carries the current value, as a native select announces it.
        aria-label={selected ? `${ariaLabel}: ${selected.label}` : ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className={`${styles.selectBtn} ${open ? styles.open : ''}`}
        style={{ width: '100%' }}
      >
        <span className={styles.face}>
          <span className={styles.selectValue}>{selected?.label || 'Select model'}</span>
          <ChevronDown />
        </span>
      </button>
      {open && (
        <CockpitPanel
          className={`absolute right-0 py-1 min-w-[190px] overflow-y-auto ${menuPlacement === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
          style={{ zIndex: 50, maxHeight: 192 }}
        >
          <div role="listbox" aria-label={ariaLabel} data-menu-tone="dark">
            {options.map((option) => {
              const isSelected = option.value === selected?.value;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  onClick={() => {
                    onChange(option.value);
                    onOpenChange(false);
                    triggerRef.current?.focus();
                  }}
                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <Check style={{ width: 13, height: 13, flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </CockpitPanel>
      )}
    </div>
  );
}
